/* eslint-disable no-restricted-syntax */
import {
  MedicalItem,
  MedicalResult,
  MedicalTranscriptEvent,
} from '@aws-sdk/client-transcribe-streaming';
import moment from 'moment-timezone';
import { AudioService } from './MicService';
import { AWSTranscribeResultBuilder } from './AWSTranscribeResultBuilder';
import constants from './constants';
import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { store } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { TranscriptionCallback } from './AWSTranscribeService';
import { JITSI_IFRAME_ORIGIN } from 'services/videoConfig';

const DEEPGRAM_LISTEN_URL = 'wss://api.deepgram.com/v1/listen';
const KEEP_ALIVE_INTERVAL_MS = 5_000;
const STOP_FLUSH_TIMEOUT_MS = 5_000;
// Grace period on stop before tearing down audio, so chunks captured up to the
// stop click still reach the socket (AWS keeps streaming during its finality
// poll; without this Deepgram's transcript tail is systematically clipped).
const AUDIO_DRAIN_MS = 300;
// A reconnect only refills its retry budget when the session is provably
// alive: audio chunks arrived this recently, or a Results message lands. A
// leaked/dead audio source has neither, so its budget runs out and the
// reconnect loop dies instead of minting tokens forever.
// Accepted tradeoff: 5 socket drops during one long fully-muted stretch
// (muted tracks produce all-zero chunks, filtered upstream) exhaust the
// budget and end the session — that path surfaces as an error state with a
// retry button (onProviderError), it is not silent.
const AUDIO_LIVENESS_WINDOW_SECONDS = 30;
// Cap on audio buffered during a reconnect gap. ~10s at 48kHz/16-bit mono
// (~960KB); beyond this the oldest buffered audio is dropped rather than
// grow memory unbounded if the socket never recovers.
const MAX_PENDING_AUDIO_BYTES = 1_000_000;

export type DeepgramTranscribeOptions = {
  /** Number of PCM channels in the audio stream (2 for merged local+remote telemedicine audio) */
  channels?: 1 | 2;
  /** Transcribe each channel independently — the equivalent of AWS EnableChannelIdentification */
  multichannel?: boolean;
  /** Speaker diarization on a single channel — the equivalent of AWS ShowSpeakerLabel */
  diarize?: boolean;
  /** Boost recognition of domain terms (nova-3 keyterm prompting) */
  keyterms?: string[];
  /**
   * Deepgram's aggregate formatter (currency/dates/measurements/paragraphs).
   * Default OFF: its currency/measurement heuristics were corrupting clinical
   * numbers (dose "175" → "$1.75", ferritin "1428" → "14, eight").
   * Punctuation is kept separately via `punctuate`.
   */
  smartFormat?: boolean;
  /** Spoken numbers → digits ("one seventy five" → "175") without smart_format's currency-ification. Default ON. */
  numerals?: boolean;
};

/**
 * Deepgram streaming provider (nova-3-medical) with the same public surface as
 * AwsTranscribeService: start/stop, a mutable transcriptionCallback, and
 * isVoicePartitioned(). Emits AWS-MedicalTranscriptEvent-shaped events so the
 * downstream accumulation/merge pipeline (TelemedicineTranscriptionService,
 * AppointmentTranscriptionFromAWS*) works unchanged with either provider.
 *
 * Auth: fetches a short-lived JWT from the backend on every connect (the raw
 * Deepgram API key never reaches the browser), passed via the WebSocket
 * subprotocol as ['bearer', token].
 *
 * Lifecycle: every async continuation (token fetch, socket open/close, audio
 * pump iterations) is guarded by sessionEpoch, which start()/stop() bump —
 * a stop() that lands while a connect is in flight invalidates that connect,
 * so no socket, keep-alive timer, or microphone acquisition can outlive the
 * session that created it.
 */
export class DeepgramTranscribeService {
  private socket?: WebSocket;
  private resultBuilder = new AWSTranscribeResultBuilder();
  private isStopping = false;
  private shouldBeRunning = false;
  protected onError?: (err: unknown) => void;
  private reconnectTimeouts: NodeJS.Timeout[] = [];
  private maxReconnectAttempts = 5;
  private reconnectAttempt = 0;
  private isReconnecting = false;
  private keepAliveTimer?: NodeJS.Timeout;
  // Monotonic session counter: bumped by start(), stop() and forceStop() so
  // stale async continuations from a previous session become no-ops.
  private sessionEpoch = 0;
  // Epoch of the session whose audio pump is currently running (-1 = none).
  private audioPumpEpoch = -1;
  // Deepgram timestamps restart at 0 on every connection; this offset keeps
  // StartTime monotonic across reconnects so sorting/grouping stays correct.
  private timeOffset = 0;
  private lastEventEnd = 0;
  // Audio captured while the socket is not OPEN (reconnect window), flushed
  // once it reopens. Bounded by total bytes so a socket that never recovers
  // cannot grow memory without bound — the oldest audio is dropped first.
  private pendingAudio: ArrayBuffer[] = [];
  private pendingAudioBytes = 0;
  // Unix seconds of the last audio chunk the pump received (0 = none this
  // session) — the liveness signal for refilling the reconnect budget.
  private lastAudioChunkAt = 0;

  constructor(
    protected audioProvider: AudioService,
    public transcriptionCallback?: TranscriptionCallback,
    errorCallback?: (err: unknown) => void,
    private options?: DeepgramTranscribeOptions,
  ) {
    this.onError = errorCallback;
  }

  isVoicePartitioned() {
    return !!this.options?.diarize;
  }

  async start() {
    this.sessionEpoch += 1;
    this.shouldBeRunning = true;
    this.isStopping = false;
    this.reconnectAttempt = 0;
    this.clearReconnectTimeouts();
    this.resultBuilder.reset();
    this.timeOffset = 0;
    this.lastEventEnd = 0;
    // Fresh session: stale liveness from a previous session must not refill
    // the new session's reconnect budget.
    this.lastAudioChunkAt = 0;
    this.pendingAudio = [];
    this.pendingAudioBytes = 0;
    return this.internalStart();
  }

  private buildUrl() {
    const params = new URLSearchParams({
      model: 'nova-3-medical',
      encoding: 'linear16',
      sample_rate: String(constants.AUDIO_RATE),
      channels: String(this.options?.channels ?? 1),
      interim_results: 'true',
      // smart_format defaults OFF (corrupts clinical numbers); punctuate and
      // numerals give readable, digit-formatted output without it.
      smart_format: String(this.options?.smartFormat ?? false),
      numerals: String(this.options?.numerals ?? true),
      punctuate: 'true',
    });
    if (this.options?.multichannel) {
      params.set('multichannel', 'true');
    }
    if (this.options?.diarize) {
      params.set('diarize', 'true');
    }
    const url = new URL(DEEPGRAM_LISTEN_URL);
    url.search = params.toString();
    // keyterm can repeat, which URLSearchParams.set can't express
    this.options?.keyterms?.forEach((term) => url.searchParams.append('keyterm', term));
    return url.toString();
  }

  private isCurrentSession(epoch: number) {
    return this.shouldBeRunning && epoch === this.sessionEpoch;
  }

  private async internalStart(): Promise<void> {
    const epoch = this.sessionEpoch;
    if (!this.shouldBeRunning) {
      return;
    }

    const token = await DeepgramTranscribeService.getCredentials();
    if (!token?.access_token) {
      throw new Error('Failed to get Deepgram token');
    }

    // stop() (or a newer start()) landed while the token was in flight —
    // opening a socket now would outlive the session that asked for it.
    if (!this.isCurrentSession(epoch)) {
      return;
    }

    // New connection: Deepgram timestamps restart, so shift the offset forward
    this.timeOffset = this.lastEventEnd;

    await new Promise<void>((fulfill, reject) => {
      const socket = new WebSocket(this.buildUrl(), ['bearer', token.access_token]);
      socket.binaryType = 'arraybuffer';
      this.socket = socket;
      let opened = false;

      socket.onopen = () => {
        opened = true;
        if (!this.isCurrentSession(epoch)) {
          socket.close();
          fulfill();
          return;
        }
        console.log('[Deepgram] Socket open, streaming...');
        // The budget only refills when the session is provably alive. A
        // socket can open successfully while the audio branch is dead
        // (orphaned tee follower after the session that fed it ended) —
        // Deepgram then drops the idle socket and the close handler
        // reconnects; unconditionally resetting on open made that loop
        // immortal (observed in prod: a leaked session minted a fresh token
        // every ~21s for almost two hours). Audio recently flowing = alive
        // (protects the deepgram-PRIMARY path, where a run of silent-stretch
        // drops would otherwise exhaust the budget and freeze the live
        // transcript mid-visit); a Results message (handleMessage) also
        // refills. A dead branch produces neither.
        if (
          this.lastAudioChunkAt > 0 &&
          moment().unix() - this.lastAudioChunkAt < AUDIO_LIVENESS_WINDOW_SECONDS
        ) {
          this.reconnectAttempt = 0;
        }
        // Drain audio captured while the socket was down (reconnect window)
        // before the pump resumes sending live chunks — recovers speech that
        // was previously dropped mid-reconnect.
        this.flushPendingAudio();
        this.isReconnecting = false;
        this.startKeepAlive();
        this.ensureAudioPump();
        fulfill();
      };

      socket.onmessage = (event) => {
        // Epoch (not liveness) guard: results must still be accepted during
        // this session's own stop/flush window, but a socket superseded by a
        // newer start() must not bleed its late results into the new
        // session's transcript.
        if (epoch !== this.sessionEpoch) {
          return;
        }
        try {
          this.handleMessage(event);
        } catch (err: any) {
          console.error('[Deepgram] Failed to handle message:', err);
        }
      };

      socket.onerror = (event) => {
        console.error('[Deepgram] Socket error:', event);
      };

      socket.onclose = (event) => {
        // Only this socket's own keep-alive: a stale socket from a superseded
        // session closing late must not kill the live session's timer.
        if (this.socket === socket) {
          this.stopKeepAlive();
        }
        console.log('[Deepgram] Socket closed:', event.code, event.reason);
        if (!opened) {
          reject(new Error(`Deepgram socket failed to open (${event.code} ${event.reason})`));
          return;
        }
        if (this.isCurrentSession(epoch) && !this.isStopping) {
          console.log('[Deepgram] Socket closed unexpectedly, attempting reconnection...');
          this.attemptReconnect();
        }
      };
    });
  }

  private handleMessage(event: MessageEvent) {
    if (typeof event.data !== 'string') {
      return;
    }
    const message = JSON.parse(event.data);
    if (message.type !== 'Results') {
      return;
    }
    // The connection proved useful (audio is flowing and being transcribed):
    // refill the reconnect budget so a long session can survive repeated
    // genuine network drops, without reviving the dead-audio zombie loop.
    this.reconnectAttempt = 0;

    const adapted = this.adaptResult(message);
    if (!adapted) {
      return;
    }

    const transcriptEvent: MedicalTranscriptEvent = {
      Transcript: { Results: [adapted] },
    };
    this.resultBuilder.accept({ TranscriptEvent: transcriptEvent });
    this.transcriptionCallback?.(this.resultBuilder.getCurrentTextValue(), transcriptEvent);
  }

  /**
   * Maps a Deepgram streaming Results message onto the AWS MedicalResult shape
   * consumed downstream:
   * - channel_index → ChannelId ('ch_0'/'ch_1', multichannel mode)
   * - words[].speaker → Items[].Speaker ('0'/'1', diarize mode)
   * - is_final → !IsPartial; interim results for the same audio window share a
   *   start time, which stands in for AWS's ResultId partial-grouping.
   */
  private adaptResult(message: any): MedicalResult | undefined {
    const alternative = message.channel?.alternatives?.[0];
    if (!alternative) {
      return undefined;
    }

    const channel = message.channel_index?.[0] ?? 0;
    const startTime = this.timeOffset + (message.start ?? 0);
    const endTime = startTime + (message.duration ?? 0);
    this.lastEventEnd = Math.max(this.lastEventEnd, endTime);

    const items: MedicalItem[] = (alternative.words ?? []).map(
      (word: any): MedicalItem => ({
        Content: word.punctuated_word ?? word.word,
        Speaker: word.speaker !== undefined ? String(word.speaker) : undefined,
        StartTime: this.timeOffset + word.start,
        EndTime: this.timeOffset + word.end,
        Type: 'pronunciation',
        Confidence: word.confidence,
      }),
    );

    return {
      ResultId: `dg-ch${channel}-${startTime}`,
      StartTime: startTime,
      EndTime: endTime,
      IsPartial: !message.is_final,
      ChannelId: `ch_${channel}`,
      Alternatives: [
        {
          Transcript: alternative.transcript,
          Items: items,
        },
      ],
    };
  }

  private ensureAudioPump() {
    // One pump per session; a pump that already drained (audioPumpEpoch reset
    // to -1) may be restarted by a reconnect within the same session.
    if (this.audioPumpEpoch === this.sessionEpoch) {
      return;
    }
    this.audioPumpEpoch = this.sessionEpoch;
    this.runAudioPump(this.sessionEpoch);
  }

  private async runAudioPump(epoch: number) {
    try {
      const iterable = await this.audioProvider.startRecording();
      if (!this.isCurrentSession(epoch) && !this.shouldBeRunning) {
        // The session stopped while audio acquisition (e.g. the getUserMedia
        // permission prompt) was pending — its stop() had nothing to release
        // at the time, so release the just-acquired microphone here. Skipped
        // when a newer live session owns the provider.
        Promise.resolve(this.audioProvider.stop()).catch((err) =>
          console.error('[Deepgram] Failed to release late-acquired audio:', err),
        );
        return;
      }
      for await (const chunk of iterable) {
        if (!this.isCurrentSession(epoch)) {
          // A newer session (or a stop) owns the socket now — this pump is
          // stale and must not touch shared state on its way out.
          return;
        }
        const data = chunk?.AudioEvent?.AudioChunk;
        if (!data) {
          continue;
        }
        // Liveness marker, tracked whether or not the socket can take the
        // chunk right now — mid-reconnect audio still proves the session and
        // its audio branch are alive.
        this.lastAudioChunkAt = moment().unix();
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.flushPendingAudio();
          this.socket.send(data);
        } else {
          // Socket down (reconnect window): buffer instead of dropping so gap
          // words aren't lost. Bounded — beyond the cap the oldest audio is
          // discarded, so a socket that never recovers can't grow memory.
          this.bufferPendingAudio(data);
        }
      }
      console.log('[Deepgram] Audio stream ended');
    } catch (err: any) {
      console.error('[Deepgram] Audio pump error:', err);
      if (this.isCurrentSession(epoch)) {
        this.onError?.(err);
      }
    }

    if (epoch === this.sessionEpoch) {
      // Audio drained within the live session: flush and allow a reconnect
      // (still same session) to restart the pump if audio resumes.
      this.audioPumpEpoch = -1;
      this.sendControl({ type: 'CloseStream' });
    }
  }

  private bufferPendingAudio(data: ArrayBuffer) {
    this.pendingAudio.push(data);
    this.pendingAudioBytes += data.byteLength;
    // Drop oldest until under the cap (keeps the most recent speech).
    while (this.pendingAudioBytes > MAX_PENDING_AUDIO_BYTES && this.pendingAudio.length > 1) {
      const dropped = this.pendingAudio.shift();
      this.pendingAudioBytes -= dropped?.byteLength ?? 0;
    }
  }

  private flushPendingAudio() {
    if (!this.pendingAudio.length) {
      return;
    }
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }
    for (const chunk of this.pendingAudio) {
      this.socket.send(chunk);
    }
    this.pendingAudio = [];
    this.pendingAudioBytes = 0;
  }

  private sendControl(message: { type: string }) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private startKeepAlive() {
    this.stopKeepAlive();
    // Deepgram closes the socket after ~10s without audio (NET-0001);
    // KeepAlive covers mute/silence/pre-roll gaps.
    this.keepAliveTimer = setInterval(() => {
      this.sendControl({ type: 'KeepAlive' });
    }, KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = undefined;
    }
  }

  private attemptReconnect(): void {
    if (
      this.isReconnecting ||
      !this.shouldBeRunning ||
      this.reconnectAttempt >= this.maxReconnectAttempts
    ) {
      if (this.shouldBeRunning && this.reconnectAttempt >= this.maxReconnectAttempts) {
        console.error('[Deepgram] Max reconnection attempts reached, giving up');
        this.onError?.(new Error('Max reconnection attempts reached'));
        this.forceStop(this.sessionEpoch);
      }
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempt += 1;
    const epoch = this.sessionEpoch;

    const baseDelay = 2 ** (this.reconnectAttempt - 1) * 1000;
    const jitter = Math.random() * 1000;
    const delay = Math.min(baseDelay + jitter, 30000);

    console.log(
      `[Deepgram] Reconnection attempt ${this.reconnectAttempt}/${
        this.maxReconnectAttempts
      } in ${Math.round(delay)}ms`,
    );

    const timeout = setTimeout(async () => {
      if (!this.isCurrentSession(epoch)) {
        this.isReconnecting = false;
        return;
      }

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.log('[Deepgram] Still offline, waiting for network...');
        this.isReconnecting = false;
        this.waitForOnline();
        return;
      }

      try {
        // Fresh token every connect — the previous one has a 60s TTL.
        await this.internalStart();
        console.log('[Deepgram] Successfully reconnected!');
      } catch (err: any) {
        console.error(`[Deepgram] Reconnection attempt ${this.reconnectAttempt} failed:`, err);
        this.isReconnecting = false;
        this.attemptReconnect();
      }
    }, delay);

    this.reconnectTimeouts.push(timeout);
  }

  private waitForOnline(): void {
    if (typeof window === 'undefined') {
      this.isReconnecting = false;
      this.attemptReconnect();
      return;
    }

    const epoch = this.sessionEpoch;
    const handleOnline = () => {
      window.removeEventListener('online', handleOnline);
      // A listener leaked from a stopped session must not reconnect (or
      // double-connect) whatever session is live when the network flaps.
      if (!this.isCurrentSession(epoch)) {
        return;
      }
      this.isReconnecting = false;
      this.attemptReconnect();
    };
    window.addEventListener('online', handleOnline);
  }

  private clearReconnectTimeouts(): void {
    this.reconnectTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.reconnectTimeouts = [];
  }

  /**
   * Returns true when this call actually tore the session down; false when a
   * newer start() superseded it (the stop became a no-op) — callers saving a
   * transcript snapshot must skip it in that case, since the service state
   * now belongs to the newer session.
   */
  async stop(): Promise<boolean> {
    // Every await below re-checks that no newer start() superseded this stop:
    // a superseded stop must become a no-op instead of tearing down the new
    // session (the epoch only advances in start() and guarded forceStop()).
    const epoch = this.sessionEpoch;

    if (this.isStopping) {
      console.log('[Deepgram] already stopping');
      return false;
    }
    this.isStopping = true;
    // Cancel pending reconnect timers immediately — a give-up forceStop firing
    // during the drain below would bump the epoch out from under this stop.
    this.clearReconnectTimeouts();
    let completed = false;

    try {
      // Let the pump flush audio captured up to the stop click before the
      // provider is torn down — parity with AWS, which keeps streaming during
      // its finality poll; otherwise the transcript tail gets clipped.
      await new Promise((resolve) => setTimeout(resolve, AUDIO_DRAIN_MS));
      if (epoch !== this.sessionEpoch) {
        return false;
      }
      this.shouldBeRunning = false;
      this.clearReconnectTimeouts();

      await this.audioProvider.stop();
      if (epoch !== this.sessionEpoch) {
        return false;
      }
      // Ask for the final flush directly rather than relying on the pump to
      // observe the stream ending — some audio providers destroy their stream
      // on stop without the pump's iterable ever completing.
      this.sendControl({ type: 'CloseStream' });
      await this.waitForSocketClose(STOP_FLUSH_TIMEOUT_MS);
    } catch (err: any) {
      console.error('[Deepgram] Error during stop:', err);
    } finally {
      // Scoped to our own session: a superseded stop must not reset the
      // isStopping guard of (or tear down) a newer session mid-stop.
      if (epoch === this.sessionEpoch) {
        this.isStopping = false;
        this.resultBuilder.finalizePartial();
        this.forceStop(epoch);
        completed = true;
      }
    }
    return completed;
  }

  private waitForSocketClose(timeoutMs: number) {
    const socket = this.socket;
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      return Promise.resolve();
    }
    return new Promise<void>((fulfill) => {
      const timeout = setTimeout(() => {
        fulfill();
      }, timeoutMs);
      socket.addEventListener('close', () => {
        clearTimeout(timeout);
        fulfill();
      });
    });
  }

  private forceStop(epoch: number) {
    // Scoped to the session that requested it — a forceStop arriving after a
    // newer start() (e.g. from a superseded stop's finally, or a stale
    // reconnect give-up) must not kill the live session.
    if (epoch !== this.sessionEpoch) {
      return;
    }
    try {
      this.shouldBeRunning = false;
      this.sessionEpoch += 1;
      this.stopKeepAlive();
      this.clearReconnectTimeouts();
      this.isReconnecting = false;
      this.isStopping = false;
      // Release the audio source: on the give-up path (max reconnects) nothing
      // else will, and stopping an already-stopped provider is safe.
      Promise.resolve(this.audioProvider.stop()).catch((err) =>
        console.error('[Deepgram] Failed to stop audio provider:', err),
      );
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        this.socket.close();
      }
      this.socket = undefined;
      this.pendingAudio = [];
      this.pendingAudioBytes = 0;
    } catch (e: unknown) {
      console.log(e);
      this.onError?.(e);
    }
  }

  static async getCredentials() {
    return getUnwrappedDispatch(store.dispatch)(api.AI.getDeepgramToken());
  }
}

/**
 * Deepgram variant of JitsiIFrameTranscriptionService: performs the same
 * audio-ready handshake with the Jitsi iframe before starting the stream.
 * The handshake listener checks the iframe origin, is removed on stop() (so a
 * late or replayed 'audio-ready' can't restart a stopped stream), and start
 * failures are routed to the error callback instead of being unhandled.
 */
export class JitsiIFrameDeepgramTranscriptionService extends DeepgramTranscribeService {
  private handshakeListener?: (ev: MessageEvent<any>) => void;

  override async start(_iframe?: HTMLIFrameElement) {
    this.clearHandshakeListener();

    const onAudioReady = (ev: MessageEvent<any>) => {
      if (ev.origin !== JITSI_IFRAME_ORIGIN) {
        return;
      }
      if (ev.data?.type !== 'audio-ready') {
        return;
      }
      this.clearHandshakeListener();
      this.sendAudioReady(_iframe);
      super.start().catch((err: unknown) => {
        console.error('[Deepgram] Failed to start after audio-ready:', err);
        this.onError?.(err);
      });
    };

    this.handshakeListener = onAudioReady;
    window.addEventListener('message', onAudioReady);

    this.sendAudioReady(_iframe);
  }

  override async stop() {
    this.clearHandshakeListener();
    return super.stop();
  }

  private clearHandshakeListener() {
    if (this.handshakeListener) {
      window.removeEventListener('message', this.handshakeListener);
      this.handshakeListener = undefined;
    }
  }

  private sendAudioReady(iframe?: HTMLIFrameElement) {
    const targetIframe =
      iframe ?? (document.querySelector("iframe[id*='jitsiConference']") as HTMLIFrameElement);
    targetIframe?.contentWindow?.postMessage(
      {
        type: 'audio-receiver-ready',
        origin: window.origin,
      },
      '*',
    );
  }
}
